import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { 
  Check, Sparkles, Loader2, IndianRupee, Users, Film, 
  FileText, Crown, Zap, X, ArrowRight, Rocket
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../config/api';
import { EVENTS, track } from '@/lib/analytics';

const API = API_BASE;

// Razorpay script loader
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// Pricing version for all NEW upgrade checkouts (Task A, PHASE_0_3_BUILD_SPEC.md).
// v1 pricing was retired (V1_PRICING_CLEANUP_PLAN.md) — the backend serves a
// single price book; the old pricing_version param/field is gone.

// Pro features to display (films/seats text is overridden from the live
// plans response so it always matches the active price book)
const PRO_FEATURES = [
  { icon: Film, key: 'films', text: '3 active films', description: 'Active film slots in this workspace' },
  { icon: Users, key: 'seats', text: '5 team seats', description: 'Invite collaborators with role-based access' },
  { icon: FileText, text: 'Script upload & parsing', description: 'Import scripts and map them to scenes automatically' },
  { icon: Zap, text: 'Team collaboration', description: 'Workspace and film-level member management' },
  { icon: Crown, text: 'Export & reports', description: 'Download data and production reports' },
];

export const UpgradeWorkspaceModal = ({ 
  open, 
  onOpenChange, 
  workspaceId,
  workspaceName,
  trigger, // What triggered the modal (e.g., 'script_upload', 'second_film', 'collaboration')
  onUpgradeComplete
}) => {
  const { loadWorkspaces } = useWorkspace();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState(null);
  const [razorpayConfig, setRazorpayConfig] = useState(null);

  // Load Razorpay config and plans
  useEffect(() => {
    if (open && workspaceId) {
      loadPlansAndConfig();
    }
  }, [open, workspaceId]);

  // Funnel: which friction point opened the paywall (script_upload, second_film, …)
  useEffect(() => {
    if (open) track(EVENTS.UPGRADE_MODAL_OPENED, { trigger: trigger || 'unknown', workspace_id: workspaceId });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPlansAndConfig = async () => {
    try {
      const [configRes, plansRes] = await Promise.all([
        axios.get(`${API}/razorpay/config`),
        axios.get(`${API}/workspaces/${workspaceId}/upgrade/plans`, {
          headers: getAuthHeaders()
        })
      ]);
      setRazorpayConfig(configRes.data);
      setPlans(plansRes.data);
    } catch (error) {
      console.error('Failed to load upgrade info:', error);
      toast.error('Failed to load upgrade options');
    }
  };

  // Prices come from the /upgrade/plans response (sourced from the backend's
  // single pricing registry) so displayed amounts always match what
  // create-subscription will actually charge.
  const proPlan = plans?.plans?.find((p) => (p.plan_id || '').startsWith('plan_pro')) || null;

  const getPrice = (cycle) => {
    if (!proPlan) return 0;
    return cycle === 'yearly' ? (proPlan.price_yearly || 0) : (proPlan.price_monthly || 0);
  };

  const handleUpgrade = async (targetPlan = 'plan_pro') => {
    setLoading(true);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        setLoading(false);
        return;
      }

      // Create subscription (recurring billing) — target_plan stays
      // tier-level and the server resolves the effective plan id
      // (plan_pro_v2) from the single price book.
      const subscriptionResponse = await axios.post(
        `${API}/workspaces/${workspaceId}/upgrade/create-subscription`,
        {
          target_plan: targetPlan,
          billing_cycle: billingCycle
        },
        { headers: getAuthHeaders() }
      );

      const subscriptionData = subscriptionResponse.data;

      // Close the modal BEFORE opening Razorpay to prevent z-index conflicts
      onOpenChange(false);

      // Small delay to ensure modal is fully closed
      await new Promise(resolve => setTimeout(resolve, 100));

      // Configure Razorpay options for subscription
      const options = {
        key: razorpayConfig?.key_id,
        subscription_id: subscriptionData.subscription_id,
        name: 'FrameFlow',
        description: `${subscriptionData.target_plan} Plan - ${billingCycle === 'yearly' ? 'Annual' : 'Monthly'} Subscription`,
        handler: async function (response) {
          try {
            // Verify subscription payment and upgrade workspace
            const verifyResponse = await axios.post(
              `${API}/workspaces/${workspaceId}/upgrade/verify-subscription`,
              {
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                transaction_id: subscriptionData.transaction_id
              },
              { headers: getAuthHeaders() }
            );

            if (verifyResponse.data.status === 'success') {
              toast.success(verifyResponse.data.message);
              
              // Refresh workspaces
              await loadWorkspaces();
              
              // Callback
              if (onUpgradeComplete) {
                onUpgradeComplete(verifyResponse.data);
              }
            } else {
              toast.error(verifyResponse.data.message || 'Subscription activation failed');
            }
          } catch (error) {
            console.error('Subscription verification failed:', error);
            toast.error('Subscription verification failed. Please contact support.');
          }
          setLoading(false);
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
            toast.info('Subscription cancelled');
          }
        },
        prefill: {
          name: workspaceName || 'Workspace'
        },
        theme: {
          color: '#F59E0B' // FrameFlow primary color
        }
      };

      // Open Razorpay checkout for subscription
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      setLoading(false);
    } catch (error) {
      console.error('Subscription creation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create subscription');
      setLoading(false);
    }
  };

  // Get trigger-specific messaging
  const getTriggerMessage = () => {
    switch (trigger) {
      case 'script_upload':
        return 'Script upload is a Pro feature. Upgrade to import scripts automatically.';
      case 'second_film':
        return 'Free workspaces can have 1 film. Upgrade to create unlimited films.';
      case 'collaboration':
      case 'invite_member':
        return 'Team collaboration is a Pro feature. Upgrade to invite team members.';
      default:
        return 'Unlock powerful features for your workspace.';
    }
  };

  const price = getPrice(billingCycle);
  const monthlyPrice = getPrice('monthly');
  const yearlyPrice = getPrice('yearly');
  const monthlyEquivalent = yearlyPrice / 12;
  const yearlySaving = Math.round(monthlyPrice * 12 - yearlyPrice);

  // Films/seats copy comes from the live plans response so it matches the
  // v2 limits (3 active films / 5 seats) instead of stale v1 copy.
  const displayedFeatures = PRO_FEATURES.map((f) => {
    if (f.key && proPlan?.features?.[f.key]) {
      return { ...f, text: proPlan.features[f.key] };
    }
    return f;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-primary/20 via-primary/10 to-transparent p-6 border-b">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Rocket className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-xl">Upgrade this Workspace to Pro</DialogTitle>
                <DialogDescription className="text-sm mt-1">
                  {workspaceName || 'Your workspace'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>

        <div className="p-6 space-y-6">
          {/* Trigger message */}
          {trigger && (
            <div className="bg-muted/50 rounded-lg p-4 border border-border">
              <p className="text-sm text-muted-foreground">{getTriggerMessage()}</p>
            </div>
          )}

          {/* Features */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              What you'll unlock
            </h3>
            <div className="space-y-2">
              {displayedFeatures.map((feature, index) => (
                <div key={index} className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{feature.text}</p>
                    <p className="text-xs text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Billing cycle toggle */}
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-2 p-1 bg-muted rounded-lg">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  billingCycle === 'yearly'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Yearly
                {yearlySaving > 0 && (
                  <span className="ml-1 text-xs text-green-600 font-normal">(Save ₹{yearlySaving.toLocaleString()})</span>
                )}
              </button>
            </div>

            {/* Price display */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <IndianRupee className="h-6 w-6" />
                <span className="text-4xl font-bold">
                  {billingCycle === 'monthly' 
                    ? Math.round(price).toLocaleString() 
                    : Math.round(monthlyEquivalent).toLocaleString()}
                </span>
                <span className="text-muted-foreground">/month</span>
              </div>
              {billingCycle === 'yearly' && (
                <p className="text-sm text-muted-foreground mt-1">
                  Billed ₹{Math.round(yearlyPrice).toLocaleString()} annually
                </p>
              )}
            </div>
          </div>

          {/* CTA Button */}
          <Button 
            onClick={() => handleUpgrade('plan_pro')} 
            disabled={loading}
            className="w-full h-12 text-base font-semibold"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-5 w-5" />
                Upgrade Workspace
                <ArrowRight className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>

          {/* Footer note */}
          <p className="text-xs text-center text-muted-foreground">
            Your existing films, scenes, and shots will remain unchanged.
            Cancel anytime.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeWorkspaceModal;
